import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { FaClipboard } from "react-icons/fa";
import { useWallet } from "../contexts/WalletContext";
import {
  SOCIAL_RECOVERY_MODULE_ADDRESS,
  SOCIAL_RECOVERY_MODULE_ABI,
} from "../utils/Constants";
import { generateSignHashUrl } from "../utils/helpers";

import "./CreateHashForm.css";
import ConnectWalletButton from "./ConnectWalletButton";
import { readContract, http, createConfig } from "@wagmi/core";
import { useChainId } from "wagmi";
import { baseSepolia } from "@wagmi/core/chains";
import { metaMask } from "@wagmi/connectors";
import {
  CONTRACT_CONFIG,
  isSupportedChain,
  getContractAddress,
  getChainName,
  SUPPORTED_CHAINS,
} from "../utils/chainConfig";

function CreateHashForm({ onGenerateHash, isConnected, address }) {
  const [axrWalletAddress, setAxrWalletAddress] = useState("");
  const [newOwner, setNewOwner] = useState("");
  const [axrWalletError, setAxrWalletError] = useState("");
  const [newOwnerError, setNewOwnerError] = useState("");
  const [generatedHash, setGeneratedHash] = useState("");
  const [copied, setCopied] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState(""); // Added error message state

  const chainId = useChainId();

  const { signer, network } = useWallet();

  const isValidAddress = (address) => {
    return ethers.isAddress(address);
  };

  useEffect(() => {
    if (axrWalletAddress && !isValidAddress(axrWalletAddress)) {
      setAxrWalletError("Please enter a valid Ethereum address");
    } else {
      setAxrWalletError("");
    }
  }, [axrWalletAddress]);

  useEffect(() => {
    if (newOwner && !isValidAddress(newOwner)) {
      setNewOwnerError("Please enter a valid Ethereum address");
    } else {
      setNewOwnerError("");
    }
  }, [newOwner]);

  // Add chain support check
  useEffect(() => {
    if (chainId && !isSupportedChain(chainId)) {
      setErrorMessage(
        `Please switch to a supported chain. Current chain (${getChainName(
          chainId
        )}) is not supported.`
      );
    } else {
      setErrorMessage("");
    }
  }, [chainId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneratedHash("");
    setGeneratedUrl("");
    setErrorMessage(""); // Clear any existing error messages

    if (axrWalletError || newOwnerError) {
      return;
    }

    if (!isSupportedChain(chainId)) {
      setErrorMessage(
        `Please switch to a supported chain. Current chain (${getChainName(
          chainId
        )}) is not supported.`
      );
      return;
    }

    try {
      const contractAddress = getContractAddress(chainId);
      const config = createConfig({
        chains: [SUPPORTED_CHAINS.find((chain) => chain.id === chainId)],
        transports: {
          [chainId]: http(),
        },
      });

      const hash = await readContract(config, {
        abi: CONTRACT_CONFIG.abi,
        address: contractAddress,
        functionName: "getHash",
        args: [
          ethers.getAddress(axrWalletAddress),
          ethers.getAddress(newOwner),
        ],
      });

      setGeneratedHash(hash);
      const url = generateSignHashUrl(
        window.location.origin,
        axrWalletAddress,
        getChainName(chainId),
        hash
      );
      setGeneratedUrl(url);
    } catch (err) {
      console.error("Error generating hash:", err);
      setErrorMessage("Error generating hash. Please try again.");
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      <h2>Create Hash for AXR Wallet Recovery</h2>
      <ConnectWalletButton />
      <div className="form-group">
        <label>AXR Wallet Address:</label>
        <input
          type="text"
          value={axrWalletAddress}
          onChange={(e) => setAxrWalletAddress(e.target.value)}
          required
        />
        {axrWalletError && (
          <div className="error-message">{axrWalletError}</div>
        )}
      </div>
      <div className="form-group">
        <label>New Owner Address:</label>
        <input
          type="text"
          value={newOwner}
          onChange={(e) => setNewOwner(e.target.value)}
          required
        />
        {newOwnerError && <div className="error-message">{newOwnerError}</div>}
      </div>
      <button
        type="submit"
        disabled={axrWalletError || newOwnerError || !isConnected}
      >
        Generate Hash
      </button>
      {generatedUrl && (
        <div className="hash-result">
          <h3>Generated Sign Hash Link</h3>
          <div className="hash-container" onClick={copyToClipboard}>
            <span>{generatedUrl}</span>
            <FaClipboard className="clipboard-icon" />
          </div>
          {copied && <div className="copied-message">Copied to clipboard!</div>}
        </div>
      )}
    </form>
  );
}

export default CreateHashForm;
